import { useMutation, type UseMutationOptions } from '@tanstack/react-query'

import {
  buildingPhotosApi,
  type PhotoDeleteResponse,
  type PhotoUploadResponse,
} from './building-photos.api'

interface UploadPhotoParams {
  file: File
  buildingUuid: string
}

interface UploadPhotoWithProgressParams extends UploadPhotoParams {
  onProgress?: (progress: number) => void
  onAbortReady?: (abort: () => void) => void
}

interface DeletePhotoParams {
  buildingUuid: string
  fileName: string
}

/**
 * Hook for uploading building photos without progress tracking
 */
export const useUploadBuildingPhoto = (
  options?: UseMutationOptions<PhotoUploadResponse, Error, UploadPhotoParams>
) => {
  return useMutation({
    mutationFn: ({ file, buildingUuid }: UploadPhotoParams) =>
      buildingPhotosApi.uploadPhoto(file, buildingUuid),
    ...options,
  })
}

/**
 * Hook for uploading building photos with progress tracking
 */
export const useUploadBuildingPhotoWithProgress = (
  options?: UseMutationOptions<PhotoUploadResponse, Error, UploadPhotoWithProgressParams>
) => {
  return useMutation({
    mutationFn: ({
      file,
      buildingUuid,
      onProgress,
      onAbortReady,
    }: UploadPhotoWithProgressParams) => {
      const { promise, abort } = buildingPhotosApi.uploadPhotoWithProgress(
        file,
        buildingUuid,
        onProgress
      )

      // Provide the abort function to the caller
      if (onAbortReady) {
        onAbortReady(abort)
      }

      return promise
    },
    ...options,
  })
}

/**
 * Hook for deleting building photos
 */
export const useDeleteBuildingPhoto = (
  options?: UseMutationOptions<PhotoDeleteResponse | null, Error, DeletePhotoParams>
) => {
  return useMutation({
    mutationFn: ({ buildingUuid, fileName }: DeletePhotoParams) =>
      buildingPhotosApi.deletePhoto(buildingUuid, fileName),
    ...options,
  })
}
